import { ensureAnchors } from './RegexValidationUtils';

export type SegmentType = 'exactText' | 'digits' | 'letters' | 'alphanumeric' | 'separator';

export enum SegmentValidationErrorCode {
    TEXT_VALUE_REQUIRED = 'TEXT_VALUE_REQUIRED',
    TEXT_VALUE_INVALID = 'TEXT_VALUE_INVALID',
    MIN_LENGTH_INVALID = 'MIN_LENGTH_INVALID',
    MAX_LENGTH_INVALID = 'MAX_LENGTH_INVALID',
    MIN_EXCEEDS_MAX = 'MIN_EXCEEDS_MAX',
    INVALID_SEPARATOR = 'INVALID_SEPARATOR',
    UNKNOWN_TYPE = 'UNKNOWN_TYPE',
}

export interface PatternSegment {
    id: string;
    type: SegmentType;
    value: string;
    minLength?: number;
    maxLength?: number;
}

const SEPARATOR_NAMES: Record<string, string> = {
    '-': 'dash',
    _: 'underscore',
    '.': 'period',
    '#': 'hash',
};

const ALLOWED_SEPARATORS = new Set(Object.keys(SEPARATOR_NAMES));

const REGEX_METACHARACTERS = /[.*+?^${}()|[\]\\]/g;

export function escapeRegexChars(str: string): string {
    return str.replace(REGEX_METACHARACTERS, '\\$&');
}

function quantifier(min?: number, max?: number): string {
    if (min != null && max != null) {
        if (min === max) return `{${min}}`;
        return `{${min},${max}}`;
    }
    if (min != null) return `{${min},}`;
    return '+';
}

export function segmentsToRegex(segments: PatternSegment[]): string {
    if (segments.length === 0) {
        return '';
    }

    const fragments: string[] = segments.map((segment) => {
        switch (segment.type) {
            case 'exactText':
                return escapeRegexChars(segment.value);
            case 'digits':
                return `\\d${quantifier(segment.minLength, segment.maxLength)}`;
            case 'letters':
                return `[a-zA-Z]${quantifier(segment.minLength, segment.maxLength)}`;
            case 'alphanumeric':
                return `[a-zA-Z0-9]${quantifier(segment.minLength, segment.maxLength)}`;
            case 'separator':
                return `\\${segment.value}`;
            default:
                return '';
        }
    });

    return fragments.join('');
}

export type TranslateFunction = (key: string, params?: Record<string, string>) => string;

function unitLabel(t: TranslateFunction, unitKey: string, count: number): string {
    return count === 1 ? t(`description-unit-${unitKey}-singular`) : t(`description-unit-${unitKey}`);
}

function describeRange(t: TranslateFunction, min?: number, max?: number, unitKey?: string): string {
    const unit = unitKey || 'characters';
    if (min != null && max != null) {
        if (min === max) return t(`description-exactly`, { count: String(min), unit: unitLabel(t, unit, min) });
        return t(`description-range`, { min: String(min), max: String(max), unit: t(`description-unit-${unit}`) });
    }
    if (min != null) return t(`description-minOrMore`, { min: String(min), unit: t(`description-unit-${unit}`) });
    return t(`description-oneOrMore`, { unit: t(`description-unit-${unit}`) });
}

function describeSegment(t: TranslateFunction, segment: PatternSegment): string {
    switch (segment.type) {
        case 'exactText':
            return `"${segment.value}"`;
        case 'digits':
            return describeRange(t, segment.minLength, segment.maxLength, 'digits');
        case 'letters':
            return describeRange(t, segment.minLength, segment.maxLength, 'letters');
        case 'alphanumeric':
            return describeRange(t, segment.minLength, segment.maxLength, 'alphanumeric');
        case 'separator':
            return t(`description-separator-${SEPARATOR_NAMES[segment.value]}`) || segment.value;
        default:
            return '';
    }
}

export function segmentsToDescription(segments: PatternSegment[], t: TranslateFunction): string {
    if (segments.length === 0) {
        return '';
    }

    return segments.map((s) => describeSegment(t, s)).join(' + ');
}

function nextId(): string {
    return `seg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

interface ParseMatch {
    segment: PatternSegment;
    nextPos: number;
}

function tryMatchDigits(pattern: string, pos: number): ParseMatch | null {
    const rangeMatch = pattern.slice(pos).match(/^\\d\{(\d+),(\d+)\}/);
    if (rangeMatch) {
        return {
            segment: {
                id: nextId(),
                type: 'digits',
                value: '',
                minLength: Number(rangeMatch[1]),
                maxLength: Number(rangeMatch[2]),
            },
            nextPos: pos + rangeMatch[0].length,
        };
    }
    const exactMatch = pattern.slice(pos).match(/^\\d\{(\d+)\}/);
    if (exactMatch) {
        const n = Number(exactMatch[1]);
        return {
            segment: { id: nextId(), type: 'digits', value: '', minLength: n, maxLength: n },
            nextPos: pos + exactMatch[0].length,
        };
    }
    return null;
}

function tryMatchLetters(pattern: string, pos: number): ParseMatch | null {
    const rangeMatch = pattern.slice(pos).match(/^\[a-zA-Z\]\{(\d+),(\d+)\}/);
    if (rangeMatch) {
        return {
            segment: {
                id: nextId(),
                type: 'letters',
                value: '',
                minLength: Number(rangeMatch[1]),
                maxLength: Number(rangeMatch[2]),
            },
            nextPos: pos + rangeMatch[0].length,
        };
    }
    const exactMatch = pattern.slice(pos).match(/^\[a-zA-Z\]\{(\d+)\}/);
    if (exactMatch) {
        const n = Number(exactMatch[1]);
        return {
            segment: { id: nextId(), type: 'letters', value: '', minLength: n, maxLength: n },
            nextPos: pos + exactMatch[0].length,
        };
    }
    return null;
}

function tryMatchAlphanumeric(pattern: string, pos: number): ParseMatch | null {
    const rangeMatch = pattern.slice(pos).match(/^\[a-zA-Z0-9\]\{(\d+),(\d+)\}/);
    if (rangeMatch) {
        return {
            segment: {
                id: nextId(),
                type: 'alphanumeric',
                value: '',
                minLength: Number(rangeMatch[1]),
                maxLength: Number(rangeMatch[2]),
            },
            nextPos: pos + rangeMatch[0].length,
        };
    }
    const exactMatch = pattern.slice(pos).match(/^\[a-zA-Z0-9\]\{(\d+)\}/);
    if (exactMatch) {
        const n = Number(exactMatch[1]);
        return {
            segment: { id: nextId(), type: 'alphanumeric', value: '', minLength: n, maxLength: n },
            nextPos: pos + exactMatch[0].length,
        };
    }
    return null;
}

function tryMatchEscapedChar(pattern: string, pos: number): ParseMatch | null {
    if (pattern[pos] !== '\\' || pos + 1 >= pattern.length) {
        return null;
    }

    const escapedChar = pattern[pos + 1];

    // Check if it's a known separator
    if (ALLOWED_SEPARATORS.has(escapedChar)) {
        return {
            segment: { id: nextId(), type: 'separator', value: escapedChar },
            nextPos: pos + 2,
        };
    }

    // It's an escaped metacharacter used in exact text
    return {
        segment: { id: nextId(), type: 'exactText', value: escapedChar },
        nextPos: pos + 2,
    };
}

function isRegexMetacharacter(ch: string): boolean {
    return '.*+?^${}()|[]\\'.indexOf(ch) !== -1;
}

function tryMatchExactText(pattern: string, pos: number): ParseMatch | null {
    const ch = pattern[pos];

    // If it's a regex metacharacter that isn't escaped, this is an unsupported construct
    if (isRegexMetacharacter(ch)) {
        return null;
    }

    // Consume consecutive literal characters
    let text = '';
    let currentPos = pos;

    while (currentPos < pattern.length) {
        const currentChar = pattern[currentPos];

        // If we hit a backslash, stop — next segment will handle it
        if (currentChar === '\\') {
            break;
        }

        // If we hit a regex metacharacter, stop
        if (isRegexMetacharacter(currentChar)) {
            break;
        }

        // Check if the next characters form a known pattern (digits, letters, etc.)
        if (currentPos > pos) {
            if (
                tryMatchDigits(pattern, currentPos) ||
                tryMatchLetters(pattern, currentPos) ||
                tryMatchAlphanumeric(pattern, currentPos)
            ) {
                break;
            }
        }

        text += currentChar;
        currentPos += 1;
    }

    if (text.length === 0) {
        return null;
    }

    return {
        segment: { id: nextId(), type: 'exactText', value: text },
        nextPos: currentPos,
    };
}

export function regexToSegments(regex: string): PatternSegment[] | null {
    let pattern = regex;

    if (pattern.startsWith('^')) {
        pattern = pattern.slice(1);
    }
    if (pattern.endsWith('$')) {
        pattern = pattern.slice(0, -1);
    }

    if (pattern.length === 0) {
        return [];
    }

    const segments: PatternSegment[] = [];
    let pos = 0;

    while (pos < pattern.length) {
        const match =
            tryMatchDigits(pattern, pos) ??
            tryMatchLetters(pattern, pos) ??
            tryMatchAlphanumeric(pattern, pos) ??
            tryMatchEscapedChar(pattern, pos) ??
            tryMatchExactText(pattern, pos);

        if (match == null) {
            return null;
        }

        segments.push(match.segment);
        pos = match.nextPos;
    }

    // Merge consecutive exactText segments
    const merged: PatternSegment[] = [];
    segments.forEach((seg) => {
        const last = merged.length > 0 ? merged[merged.length - 1] : null;
        if (seg.type === 'exactText' && last && last.type === 'exactText') {
            last.value += seg.value;
        } else {
            merged.push(seg);
        }
    });

    return merged;
}

export function testPatternAgainstSamples(
    regex: string,
    samples: string[]
): Array<{ sample: string; matches: boolean }> {
    const compiledRegex = new RegExp(ensureAnchors(regex));

    return samples.map((sample) => ({
        sample,
        matches: compiledRegex.test(sample),
    }));
}

export interface SegmentValidationResult {
    isValid: boolean;
    errorCode?: SegmentValidationErrorCode;
}

export function validateSegment(segment: PatternSegment): SegmentValidationResult {
    switch (segment.type) {
        case 'exactText':
            if (!segment.value || segment.value.length === 0) {
                return { isValid: false, errorCode: SegmentValidationErrorCode.TEXT_VALUE_REQUIRED };
            }
            if (!/^[a-zA-Z0-9]+$/.test(segment.value)) {
                return { isValid: false, errorCode: SegmentValidationErrorCode.TEXT_VALUE_INVALID };
            }
            return { isValid: true };

        case 'digits':
        case 'letters':
        case 'alphanumeric': {
            const { minLength, maxLength } = segment;
            if (minLength != null && (!Number.isInteger(minLength) || minLength < 1 || minLength > 20)) {
                return { isValid: false, errorCode: SegmentValidationErrorCode.MIN_LENGTH_INVALID };
            }
            if (maxLength != null && (!Number.isInteger(maxLength) || maxLength < 1 || maxLength > 20)) {
                return { isValid: false, errorCode: SegmentValidationErrorCode.MAX_LENGTH_INVALID };
            }
            if (minLength != null && maxLength != null && minLength > maxLength) {
                return { isValid: false, errorCode: SegmentValidationErrorCode.MIN_EXCEEDS_MAX };
            }
            return { isValid: true };
        }

        case 'separator':
            if (!ALLOWED_SEPARATORS.has(segment.value)) {
                return { isValid: false, errorCode: SegmentValidationErrorCode.INVALID_SEPARATOR };
            }
            return { isValid: true };

        default:
            return { isValid: false, errorCode: SegmentValidationErrorCode.UNKNOWN_TYPE };
    }
}
